import React, { useContext, useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import { exchangeCodeForToken, fetchIntercomJWT, getAccessToken } from '../services'
import { isAuthenticated, isSessionRejectedError, signOut, me } from '../services'
import {
  segmentIdentify,
  segmentAnonymousId,
  setBugsnagUser,
  googleTagManagerEvent,
  trackSignedIn,
  storageMode,
  getBranchAttribution,
  getAdAttribution,
  buildCampaignContext,
  CLICK_ID_KEYS,
  UTM_KEYS,
} from '../helpers'
import { growthbook } from '../services/growthbook'
import Intercom from '@intercom/messenger-js-sdk';
import { claimUserCredit } from '../services'

export const UserContext = React.createContext({})
export const useUserContext = () => useContext(UserContext)

export const UserContextProvider = ({ children, initialUser = undefined }) => {
  const { query } = useRouter()
  const { magic } = query
  const localStorageMode = storageMode('localStorage')
  const branchPromoCode = localStorageMode.getItem('branch_promo_code')

  const [user, _setUser] = useState(initialUser)

  // update state and identify to bugsnag & segment
  const setUser = useCallback((user) => {
    _setUser(user)

    // identifies a user to segment and bugsnag
    if (user) {
      try {
        const branchAttribution = getBranchAttribution()
        const adAttribution = getAdAttribution()
        const campaignContext = buildCampaignContext(branchAttribution, adAttribution)

        // build UTM and click ID traits from ad attribution
        const attributionTraits = {}
        if (adAttribution) {
          // identify merges traits, so send all five UTMs and null the absent
          // ones; otherwise a partial or click-ID-only visit would keep the
          // previous visit's utm_campaign. Click IDs are only ever added, not
          // nulled: ad platforms match conversions against them for weeks
          // after the click, so a later email visit must not erase a gclid
          UTM_KEYS.forEach(key => {
            attributionTraits[key] = adAttribution[key] || null
          })
          CLICK_ID_KEYS.forEach(key => {
            if (adAttribution[key]) {
              attributionTraits[key] = adAttribution[key]
            }
          })
        }

        segmentIdentify(user.id, {
          id: user.id,
          email: user.email,
          phone: user.phone,
          fullName: user.fullName,
          firstName: user.nameFirst,
          lastName: user.nameLast,
          name: user.fullName,
          ...(branchAttribution && {
            branch_referring_link: branchAttribution['~referring_link'],
            branch_first_session: branchAttribution['+is_first_session'],
            branch_match_guaranteed: branchAttribution['+match_guaranteed'],
            branch_click_timestamp: branchAttribution['+click_timestamp'],
            branch_link_click_id: branchAttribution['+click_id'],
          }),
          ...attributionTraits,
        }, campaignContext, () => {
          // clear the main keys only once Segment has processed the identify
          // carrying them: a visitor who leaves first should send the
          // attribution on their next visit instead. Skip a key that was
          // replaced in the meantime (another tagged tab, a late Branch init)
          // so newer, unsent attribution survives.
          const storedAttribution = [
            ['branch_attribution', branchAttribution],
            ['ad_attribution', adAttribution]
          ]
          storedAttribution.forEach(([key, sentAttribution]) => {
            if (
              sentAttribution &&
              localStorageMode.getItem(key) === JSON.stringify(sentAttribution)
            ) {
              localStorageMode.removeItem(key)
            }
          })
        })

        // preserve attribution for first order tracking
        if (branchAttribution) {
          localStorageMode.setItem('branch_attribution_for_first_order', JSON.stringify(branchAttribution))
        }
        if (adAttribution) {
          localStorageMode.setItem('ad_attribution_for_first_order', JSON.stringify(adAttribution))
        }

        setBugsnagUser(user)
        googleTagManagerEvent('identify', {
          userId: user.id,
          email: user.email,
          anonymousId: segmentAnonymousId(),
        })
        growthbook.setAttributes({
          // Identity updates must retain market-level experiment targeting.
          ...growthbook.getAttributes(),
          id: user.id,
          firstName: user?.nameFirst,
          lastName: user?.nameLast,
          email: user?.email,
          anonymousId: segmentAnonymousId(),
        })
        trackSignedIn(user.email, user.id)

        fetchIntercomJWT(user.id)
          .then((jwt) => {
            if (!jwt) {
              console.log('no intercom jwt')
              return
            }

            const accessToken = getAccessToken()

            // console.log(JSON.stringify({securityToken}, null, 2))
            // console.log(jwt)            

            return Intercom({
              app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
              intercom_user_jwt: jwt,

              user_id: user.id,
              name: user.fullName,
              email: user.email,
              created_at: user.createdAt,
              auth_tokens: {
                security_token: accessToken,
              }
            });
          });

      } catch (error) {
        console.warn(error, 'identify user')
      }
    }
  }, [localStorageMode])

  // sign user in if given a magic code
  useEffect(() => {
    if (!magic) {
      return
    }
    exchangeCodeForToken(magic)
      .then(setUser)
  }, [magic, setUser])

  // function to reload the user from starbase
  const refreshUser = () => {
    if (!isAuthenticated()) {
      return
    }

    me()
      .then(user => setUser({ ...user }))
      .catch(err => {
        console.error(err)

        // Only drop the session when Starbase actually refused it. A profile fetch that never
        // completed (a dropped connection on this page load) says nothing about the token, and
        // signing out here clears storage shared by every open tab: that is how a checkout in
        // another tab ended up sending its payment request anonymously.
        if (isSessionRejectedError(err)) {
          signOut()
        }
      })
  }

  // load user if we're authenticated
  useEffect(() => {
    refreshUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user) {
      return
    }

    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      // Clear signed-in identity without discarding the selected market.
      id: undefined,
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      anonymousId: segmentAnonymousId(),
    })

  }, [user])

  useEffect(() => {
    if (user && branchPromoCode) {
      claimUserCredit(user.id, branchPromoCode)
      localStorageMode.removeItem('branch_promo_code')
    }
  }, [user, branchPromoCode, localStorageMode])

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser }}>
      {children}
    </UserContext.Provider>
  )
}
